import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Clock, Star, TrendingUp } from 'lucide-react';

const KalachakraDasa = ({ birthData }) => {
  const [dasaData, setDasaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMahadasa, setSelectedMahadasa] = useState(null);

  // Default birth data if not provided
  const defaultBirthData = {
    date: '1997-05-14',
    time: '12:17:00',
    lat: 30.3255646,
    lon: 78.0436813,
    timezone: 'Asia/Kolkata'
  };

  const finalBirthData = birthData || defaultBirthData;

  useEffect(() => {
    calculateKalachakra();
  }, []);

  const calculateKalachakra = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';
      
      console.log('Calling Kalachakra API with:', finalBirthData);
      
      const response = await axios.post(
        `${backendUrl}/api/kalachakra/calculate`,
        finalBirthData
      );

      console.log('Kalachakra API response:', response.data);

      if (response.data.success) {
        setDasaData(response.data.data);
        if (response.data.data.mahadasas && response.data.data.mahadasas.length > 0) {
          setSelectedMahadasa(response.data.data.mahadasas[0]);
        }
      } else {
        setError('API returned unsuccessful response');
      }
    } catch (err) {
      console.error('Kalachakra API Error:', err);
      setError(err.message || 'Failed to calculate Kalachakra Dasa');
    } finally {
      setLoading(false);
    }
  };

  const formatYMD = (years) => {
    const y = Math.floor(years);
    const remaining = years - y;
    const m = Math.floor(remaining * 12);
    const d = Math.floor(((remaining * 12) - m) * 30);
    return `${y}Y ${m}M ${d}D`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const isCurrentPeriod = (startDate, endDate) => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    return now >= start && now <= end;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-xl">Calculating Kalachakra Dasa...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-red-900 to-pink-900">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 max-w-md">
          <h2 className="text-2xl font-bold text-white mb-4">Error</h2>
          <p className="text-red-200">{error}</p>
        </div>
      </div>
    );
  }

  if (!dasaData) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 mb-6 border border-white/20">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <Star className="w-8 h-8 text-yellow-300" />
              <h1 className="text-3xl font-bold text-white">Kalachakra Dasa</h1>
            </div>
            <div className="text-right">
              <p className="text-white/80 text-sm">Nakshatra-Based Dasa System</p>
              <p className="text-white/60 text-xs">Based on Moon's Navamsa</p>
            </div>
          </div>

          {/* Birth Chart Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white/5 rounded-lg p-4">
              <p className="text-white/60 text-sm mb-1">Birth Nakshatra</p>
              <p className="text-white font-semibold text-lg">{dasaData.nakshatra}</p>
              <p className="text-white/80 text-sm">Pada {dasaData.pada}</p>
            </div>
            <div className="bg-white/5 rounded-lg p-4">
              <p className="text-white/60 text-sm mb-1">Chakra Type</p>
              <p className="text-white font-semibold text-lg">{dasaData.savya_apsavya}</p>
              <p className="text-white/80 text-sm">{dasaData.group} Group</p>
            </div>
            <div className="bg-white/5 rounded-lg p-4">
              <p className="text-white/60 text-sm mb-1">Paramayus</p>
              <p className="text-white font-semibold text-lg">{dasaData.paramayus} Years</p>
              <p className="text-white/80 text-sm">Maximum Cycle</p>
            </div>
            <div className="bg-white/5 rounded-lg p-4">
              <p className="text-white/60 text-sm mb-1">Deha - Jeeva</p>
              <p className="text-white font-semibold text-lg">{dasaData.deha} - {dasaData.jeeva}</p>
              <p className="text-white/80 text-sm">Start - End</p>
            </div>
          </div>

          {/* Current Dasa */}
          {dasaData.current_mahadasa && (
            <div className="mt-6 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-lg p-6 border border-green-400/30">
              <div className="flex items-center space-x-2 mb-3">
                <TrendingUp className="w-5 h-5 text-green-300" />
                <h3 className="text-xl font-bold text-white">Currently Running</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-white/80 text-sm mb-1">Mahadasa</p>
                  <p className="text-2xl font-bold text-white">{dasaData.current_mahadasa.rasi}</p>
                  <p className="text-white/60 text-sm mt-1">
                    {formatDate(dasaData.current_mahadasa.start_date)} - {formatDate(dasaData.current_mahadasa.end_date)}
                  </p>
                </div>
                {dasaData.current_antardasa && (
                  <div>
                    <p className="text-white/80 text-sm mb-1">Antardasa</p>
                    <p className="text-xl font-bold text-green-300">{dasaData.current_antardasa.rasi}</p>
                    <p className="text-white/60 text-sm mt-1">
                      {formatDate(dasaData.current_antardasa.start_date)} - {formatDate(dasaData.current_antardasa.end_date)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Mahadasa Timeline */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
          <div className="flex items-center space-x-3 mb-6">
            <Calendar className="w-6 h-6 text-blue-300" />
            <h2 className="text-2xl font-bold text-white">Mahadasa Timeline</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Mahadasa List */}
            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
              {dasaData.mahadasas.slice(0, 15).map((md, idx) => {
                const isCurrent = dasaData.current_mahadasa && 
                                 md.rasi === dasaData.current_mahadasa.rasi &&
                                 md.start_date === dasaData.current_mahadasa.start_date;
                const isSelected = selectedMahadasa && 
                                  md.rasi === selectedMahadasa.rasi &&
                                  md.start_date === selectedMahadasa.start_date;

                return (
                  <div
                    key={idx}
                    onClick={() => setSelectedMahadasa(md)}
                    className={`cursor-pointer p-4 rounded-lg transition-all ${
                      isCurrent 
                        ? 'bg-gradient-to-r from-green-500/30 to-emerald-500/30 border-2 border-green-400' 
                        : isSelected
                        ? 'bg-white/20 border-2 border-blue-400'
                        : 'bg-white/5 border border-white/10 hover:bg-white/10'
                    }`}
                  >
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-white/60 text-sm">#{idx + 1}</span>
                        <h3 className="text-xl font-bold text-white">{md.rasi}</h3>
                        {md.is_balance && (
                          <span className="px-2 py-1 bg-yellow-500/30 text-yellow-200 text-xs rounded">
                            Balance
                          </span>
                        )}
                        {isCurrent && (
                          <span className="px-2 py-1 bg-green-500/30 text-green-200 text-xs rounded animate-pulse">
                            Current
                          </span>
                        )}
                      </div>
                      <span className="text-white/80 text-sm">{formatYMD(md.duration)}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-white/60 text-sm">
                      <Clock className="w-4 h-4" />
                      <span>{formatDate(md.start_date)} - {formatDate(md.end_date)}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Antardasa Details */}
            <div className="bg-white/5 rounded-lg p-6">
              {selectedMahadasa ? (
                <>
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-white mb-2">
                      {selectedMahadasa.rasi} Mahadasa
                    </h3>
                    <p className="text-white/60 text-sm">
                      {formatDate(selectedMahadasa.start_date)} - {formatDate(selectedMahadasa.end_date)}
                    </p>
                    <p className="text-white/80 mt-1">Duration: {formatYMD(selectedMahadasa.duration)}</p>
                  </div>

                  <h4 className="text-lg font-semibold text-white mb-4">Antardasas (9 Sub-periods)</h4>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {selectedMahadasa.antardasas.map((ad, idx) => {
                      const isCurrentAD = dasaData.current_antardasa &&
                                         ad.rasi === dasaData.current_antardasa.rasi &&
                                         ad.start_date === dasaData.current_antardasa.start_date;

                      return (
                        <div
                          key={idx}
                          className={`p-3 rounded-lg ${
                            isCurrentAD 
                              ? 'bg-gradient-to-r from-green-500/30 to-emerald-500/30 border border-green-400'
                              : 'bg-white/5 border border-white/10'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <span className="text-white/60 text-xs mr-2">{idx + 1}.</span>
                              <span className="text-white font-semibold">{ad.rasi}</span>
                              {isCurrentAD && (
                                <span className="ml-2 px-2 py-1 bg-green-500/30 text-green-200 text-xs rounded">
                                  Running
                                </span>
                              )}
                            </div>
                            <span className="text-white/60 text-xs">{formatYMD(ad.duration)}</span>
                          </div>
                          <p className="text-white/50 text-xs mt-1">
                            {formatDate(ad.start_date)} - {formatDate(ad.end_date)}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-white/60 text-center">
                    Select a Mahadasa to view its Antardasas
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KalachakraDasa;
